/**
 * Export utilities — PDF (jsPDF + autotable) and Word DOCX.
 * Shift notes export in chronological order (oldest = #1).
 * Incidents export in incident_date order (oldest = #1).
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  WidthType, AlignmentType, HeadingLevel, BorderStyle,
} from 'docx';
import { fmtTime12 } from './time';

// ── helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return d; }
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ── PDF helpers ───────────────────────────────────────────────────────────────

function pdfHeader(doc, title, subtitle) {
  // Navy bar
  doc.setFillColor(30, 41, 59);
  doc.rect(0, 0, doc.internal.pageSize.width, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Rostery', 14, 13);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${fmtDateTime(new Date())}`, doc.internal.pageSize.width - 14, 13, { align: 'right' });

  // Title
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 34);
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(subtitle, 14, 42);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHIFT NOTES — PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function exportNotesPDF(notes, clientName = '') {
  // Sort chronologically — oldest first = #1
  const sorted = [...notes].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  if (!sorted.length) return;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const title = `Shift Notes${clientName ? ' — ' + clientName : ''}`;
  const subtitle = `${sorted.length} note${sorted.length !== 1 ? 's' : ''} in chronological order`;
  pdfHeader(doc, title, subtitle);

  const rows = sorted.map((n, i) => [
    { content: String(i + 1), styles: { halign: 'center', fontStyle: 'bold', textColor: [99, 102, 241] } },
    fmtDateTime(n.created_at),
    n.note_type || 'Note',
    n.staff_name || '—',
    n.shift_date
      ? `${fmtDate(n.shift_date)}${n.start_time ? ' ' + fmtTime12(n.start_time) : ''}${n.end_time ? '–' + fmtTime12(n.end_time) : ''}`
      : '—',
    n.note_content || '',
  ]);

  autoTable(doc, {
    startY: 50,
    head: [['#', 'Date & Time', 'Type', 'Staff', 'Shift', 'Note Content']],
    body: rows,
    columnStyles: {
      0: { cellWidth: 10 },
      1: { cellWidth: 36 },
      2: { cellWidth: 22 },
      3: { cellWidth: 30 },
      4: { cellWidth: 28 },
      5: { cellWidth: 'auto' },
    },
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
      overflow: 'linebreak',
      valign: 'top',
    },
    headStyles: {
      fillColor: [99, 102, 241],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didDrawPage: (data) => {
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 6,
        { align: 'center' }
      );
    },
  });

  const filename = `shift-notes${clientName ? '-' + clientName.replace(/\s+/g, '-').toLowerCase() : ''}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHIFT NOTES — DOCX
// ═══════════════════════════════════════════════════════════════════════════════

export async function exportNotesDocx(notes, clientName = '') {
  const sorted = [...notes].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  if (!sorted.length) return;

  const borderNone = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
  const noBorder = { top: borderNone, bottom: borderNone, left: borderNone, right: borderNone };

  const headerColor = '1e293b';
  const accentColor = '6366f1';

  const children = [
    // Title
    new Paragraph({
      children: [
        new TextRun({ text: 'Rostery — Shift Notes', bold: true, size: 32, color: headerColor }),
      ],
    }),
    new Paragraph({
      children: [
        new TextRun({ text: clientName ? `Client: ${clientName}` : '', size: 22, color: '64748b' }),
      ],
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `Generated: ${fmtDateTime(new Date())}   |   ${sorted.length} note${sorted.length !== 1 ? 's' : ''} in chronological order`, size: 18, color: '94a3b8' }),
      ],
      spacing: { after: 300 },
    }),

    // Table header
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        // Header row
        new TableRow({
          tableHeader: true,
          children: ['#', 'Date & Time', 'Type', 'Staff', 'Shift', 'Note Content'].map((h) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: h, bold: true, color: 'FFFFFF', size: 18 })],
                  alignment: AlignmentType.LEFT,
                }),
              ],
              shading: { fill: accentColor },
            })
          ),
        }),
        // Data rows
        ...sorted.map((n, i) =>
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: String(i + 1), bold: true, color: accentColor, size: 18 })], alignment: AlignmentType.CENTER })],
                shading: { fill: i % 2 === 0 ? 'ffffff' : 'f8fafc' },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: fmtDateTime(n.created_at), size: 18 })] })],
                shading: { fill: i % 2 === 0 ? 'ffffff' : 'f8fafc' },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: n.note_type || 'Note', size: 18 })] })],
                shading: { fill: i % 2 === 0 ? 'ffffff' : 'f8fafc' },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: n.staff_name || '—', size: 18 })] })],
                shading: { fill: i % 2 === 0 ? 'ffffff' : 'f8fafc' },
              }),
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({
                    text: n.shift_date
                      ? `${fmtDate(n.shift_date)}${n.start_time ? ' ' + fmtTime12(n.start_time) : ''}${n.end_time ? '–' + fmtTime12(n.end_time) : ''}`
                      : '—',
                    size: 18,
                  })],
                })],
                shading: { fill: i % 2 === 0 ? 'ffffff' : 'f8fafc' },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: n.note_content || '', size: 18 })] })],
                shading: { fill: i % 2 === 0 ? 'ffffff' : 'f8fafc' },
              }),
            ],
          })
        ),
      ],
    }),
  ];

  const wordDoc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(wordDoc);
  const filename = `shift-notes${clientName ? '-' + clientName.replace(/\s+/g, '-').toLowerCase() : ''}-${new Date().toISOString().slice(0, 10)}.docx`;
  triggerDownload(blob, filename);
}

// ═══════════════════════════════════════════════════════════════════════════════
// INCIDENTS — PDF
// ═══════════════════════════════════════════════════════════════════════════════

const TYPE_LABELS = {
  fall: 'Fall', medication_error: 'Medication Error', behavior: 'Behavioral',
  property_damage: 'Property Damage', near_miss: 'Near Miss', abuse: 'Abuse/Neglect',
  elopement: 'Elopement', medical_emergency: 'Medical Emergency',
};

export function exportIncidentsPDF(incidents, categories = []) {
  const sorted = [...incidents].sort((a, b) => {
    const da = new Date(a.incident_date || a.created_at);
    const db = new Date(b.incident_date || b.created_at);
    return da - db;
  });
  if (!sorted.length) return;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const title = 'Incident Report';
  const subtitle = `${sorted.length} incident${sorted.length !== 1 ? 's' : ''} — chronological order`;
  pdfHeader(doc, title, subtitle);

  // Summary table
  const rows = sorted.map((inc, i) => {
    const cat = categories.find(c => c.slug === inc.category);
    return [
      { content: String(i + 1), styles: { halign: 'center', fontStyle: 'bold', textColor: [239, 68, 68] } },
      fmtDate(inc.incident_date),
      inc.ticket_name || '—',
      TYPE_LABELS[inc.incident_type] || inc.incident_type || '—',
      cat?.name || inc.category || '—',
      (inc.priority || '—').toUpperCase(),
      (inc.status || '—').replace(/_/g, ' '),
      inc.client_name || '—',
      inc.staff_name || inc.assignee_name || '—',
      inc.location || '—',
    ];
  });

  autoTable(doc, {
    startY: 50,
    head: [['#', 'Date', 'Incident Name', 'Type', 'Category', 'Priority', 'Status', 'Client', 'Staff', 'Location']],
    body: rows,
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 20 },
      2: { cellWidth: 38 },
      3: { cellWidth: 22 },
      4: { cellWidth: 20 },
      5: { cellWidth: 16 },
      6: { cellWidth: 18 },
      7: { cellWidth: 25 },
      8: { cellWidth: 25 },
      9: { cellWidth: 'auto' },
    },
    styles: {
      fontSize: 8,
      cellPadding: { top: 3.5, bottom: 3.5, left: 3.5, right: 3.5 },
      overflow: 'linebreak',
      valign: 'top',
    },
    headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: [254, 242, 242] },
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Page ${data.pageNumber} of ${pageCount}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 6, { align: 'center' });
    },
  });

  // Detail pages — one section per incident
  sorted.forEach((inc, i) => {
    doc.addPage();

    // Section header
    doc.setFillColor(239, 68, 68);
    doc.rect(0, 0, doc.internal.pageSize.width, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Incident #${i + 1} — ${inc.ticket_name || 'Unnamed'}`, 14, 10);

    let y = 26;
    const col2x = doc.internal.pageSize.width / 2 + 5;

    const field = (label, value, x, currentY) => {
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(label.toUpperCase(), x, currentY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(String(value || '—'), (doc.internal.pageSize.width / 2) - 20);
      doc.text(lines, x, currentY + 5);
      return currentY + 5 + lines.length * 5 + 3;
    };

    const cat = categories.find(c => c.slug === inc.category);

    // Two-column key fields
    let y1 = y, y2 = y;
    y1 = field('Date', fmtDate(inc.incident_date), 14, y1);
    y1 = field('Time', inc.incident_time ? fmtTime12(inc.incident_time) : '—', 14, y1);
    y1 = field('Incident Type', TYPE_LABELS[inc.incident_type] || inc.incident_type || '—', 14, y1);
    y1 = field('Category', cat?.name || inc.category || '—', 14, y1);
    y1 = field('Priority', (inc.priority || '—').toUpperCase(), 14, y1);
    y1 = field('Status', (inc.status || '—').replace(/_/g, ' '), 14, y1);

    y2 = field('Client', inc.client_name || '—', col2x, y2);
    y2 = field('Staff', inc.staff_name || inc.assignee_name || '—', col2x, y2);
    y2 = field('Location', inc.location || '—', col2x, y2);
    y2 = field('NDIS Reportable', inc.ndis_reportable ? 'Yes' : 'No', col2x, y2);
    y2 = field('Reported to NDIS', inc.reported_to_ndis ? 'Yes' : 'No', col2x, y2);
    y2 = field('Due Date', fmtDate(inc.due_date), col2x, y2);

    y = Math.max(y1, y2) + 5;

    // Divider
    doc.setDrawColor(226, 232, 240);
    doc.line(14, y, doc.internal.pageSize.width - 14, y);
    y += 6;

    // Text fields
    const textBlock = (label, value) => {
      if (!value) return;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(label.toUpperCase(), 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(value, doc.internal.pageSize.width - 28);
      // page overflow check
      if (y + lines.length * 5 > doc.internal.pageSize.height - 15) {
        doc.addPage();
        y = 20;
      }
      doc.text(lines, 14, y);
      y += lines.length * 5 + 7;
    };

    textBlock('Description', inc.description);
    textBlock('Actions Taken', inc.actions_taken);
    textBlock('Witnesses', inc.witnesses);
    textBlock('Injury Details', inc.injury_details);
    textBlock('Follow-up Notes', inc.follow_up_notes);
    textBlock('Resolution Notes', inc.resolution_notes);

    // Footer
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.text(`Created: ${fmtDateTime(inc.created_at)}`, 14, doc.internal.pageSize.height - 6);
  });

  const filename = `incidents-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// ═══════════════════════════════════════════════════════════════════════════════
// INCIDENTS — DOCX
// ═══════════════════════════════════════════════════════════════════════════════

export async function exportIncidentsDocx(incidents, categories = []) {
  const sorted = [...incidents].sort((a, b) => {
    const da = new Date(a.incident_date || a.created_at);
    const db = new Date(b.incident_date || b.created_at);
    return da - db;
  });
  if (!sorted.length) return;

  const accentColor = 'ef4444';

  const heading = (text) =>
    new Paragraph({
      children: [new TextRun({ text, bold: true, size: 28, color: accentColor })],
      spacing: { before: 240, after: 80 },
    });

  const subHeading = (text) =>
    new Paragraph({
      children: [new TextRun({ text, bold: true, size: 22, color: '1e293b' })],
      spacing: { before: 160, after: 60 },
    });

  const labelValue = (label, value) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${label}: `, bold: true, size: 19, color: '64748b' }),
        new TextRun({ text: String(value || '—'), size: 19, color: '1e293b' }),
      ],
      spacing: { after: 40 },
    });

  const bodyText = (label, value) => {
    if (!value) return [];
    return [
      new Paragraph({
        children: [new TextRun({ text: label, bold: true, size: 19, color: '64748b' })],
        spacing: { before: 120, after: 40 },
      }),
      new Paragraph({
        children: [new TextRun({ text: value, size: 19, color: '334155' })],
        spacing: { after: 80 },
      }),
    ];
  };

  const divider = () =>
    new Paragraph({
      border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'e2e8f0' } },
      spacing: { before: 120, after: 120 },
      children: [],
    });

  const children = [
    new Paragraph({
      children: [new TextRun({ text: 'Rostery — Incident Report', bold: true, size: 36, color: '1e293b' })],
    }),
    new Paragraph({
      children: [new TextRun({ text: `Generated: ${fmtDateTime(new Date())}   |   ${sorted.length} incident${sorted.length !== 1 ? 's' : ''} in chronological order`, size: 18, color: '94a3b8' })],
      spacing: { after: 400 },
    }),
  ];

  sorted.forEach((inc, i) => {
    const cat = categories.find(c => c.slug === inc.category);
    children.push(heading(`Incident #${i + 1} — ${inc.ticket_name || 'Unnamed'}`));
    children.push(labelValue('Date', fmtDate(inc.incident_date)));
    children.push(labelValue('Time', inc.incident_time ? fmtTime12(inc.incident_time) : '—'));
    children.push(labelValue('Type', TYPE_LABELS[inc.incident_type] || inc.incident_type || '—'));
    children.push(labelValue('Category', cat?.name || inc.category || '—'));
    children.push(labelValue('Priority', (inc.priority || '—').toUpperCase()));
    children.push(labelValue('Status', (inc.status || '—').replace(/_/g, ' ')));
    children.push(labelValue('Client', inc.client_name || '—'));
    children.push(labelValue('Staff', inc.staff_name || inc.assignee_name || '—'));
    children.push(labelValue('Location', inc.location || '—'));
    children.push(labelValue('NDIS Reportable', inc.ndis_reportable ? 'Yes' : 'No'));
    children.push(labelValue('Reported to NDIS', inc.reported_to_ndis ? 'Yes' : 'No'));
    children.push(divider());
    bodyText('Description', inc.description).forEach(p => children.push(p));
    bodyText('Actions Taken', inc.actions_taken).forEach(p => children.push(p));
    bodyText('Witnesses', inc.witnesses).forEach(p => children.push(p));
    bodyText('Injury Details', inc.injury_details).forEach(p => children.push(p));
    bodyText('Follow-up Notes', inc.follow_up_notes).forEach(p => children.push(p));
    bodyText('Resolution Notes', inc.resolution_notes).forEach(p => children.push(p));
    if (i < sorted.length - 1) {
      children.push(new Paragraph({ children: [], pageBreakBefore: true }));
    }
  });

  const wordDoc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(wordDoc);
  triggerDownload(blob, `incidents-${new Date().toISOString().slice(0, 10)}.docx`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// INCIDENT MANAGEMENT REGISTER — XLSX (matches the printable register layout)
// ═══════════════════════════════════════════════════════════════════════════════

const REGISTER_COLUMNS = [
  { header: 'Ref No.',                       key: 'ref',       width: 8  },
  { header: 'Date',                          key: 'date',      width: 12 },
  { header: 'Time',                          key: 'time',      width: 10 },
  { header: 'Location',                      key: 'location',  width: 22 },
  { header: 'Type of Incident',              key: 'type',      width: 22 },
  { header: 'Participant(s) Involved',       key: 'clients',   width: 22 },
  { header: 'Staff Involved',                key: 'staff',     width: 20 },
  { header: 'Witnesses',                     key: 'witness',   width: 16 },
  { header: 'Incident Form Completed?',      key: 'form',      width: 12 },
  { header: 'Reported to NDIS/Other?',       key: 'reported',  width: 14 },
  { header: 'Incident Description',          key: 'desc',      width: 40 },
  { header: 'Immediate Actions Taken',       key: 'actions',   width: 32 },
  { header: 'Follow-up / Outcome',           key: 'followup',  width: 30 },
  { header: 'Further Action / Support',      key: 'further',   width: 28 },
  { header: 'Actions to Prevent Recurrence', key: 'prevent',   width: 30 },
];

const joinNames = (primary, arr) => {
  const list = [];
  if (primary) list.push(primary);
  (arr || []).forEach(x => { if (x?.name && !list.includes(x.name)) list.push(x.name); });
  return list.join(', ');
};

export async function exportIncidentRegisterXlsx(incidents, categories = []) {
  const sorted = [...incidents].sort((a, b) => {
    const da = new Date(a.incident_date || a.created_at);
    const db = new Date(b.incident_date || b.created_at);
    return da - db;
  });
  if (!sorted.length) return;

  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Rostery';
  wb.created = new Date();
  const ws = wb.addWorksheet('Incident Register', {
    views: [{ state: 'frozen', ySplit: 4 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  const nCols = REGISTER_COLUMNS.length;
  ws.columns = REGISTER_COLUMNS.map(c => ({ key: c.key, width: c.width }));

  // Title block
  const dates = sorted.map(i => new Date(i.incident_date || i.created_at)).filter(d => !isNaN(d));
  const period = dates.length ? `${fmtDate(dates[0])} – ${fmtDate(dates[dates.length - 1])}` : '';

  ws.mergeCells(1, 1, 1, nCols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = 'INCIDENT MANAGEMENT REGISTER';
  titleCell.font = { bold: true, size: 15, color: { argb: 'FFFFFFFF' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
  ws.getRow(1).height = 26;

  ws.mergeCells(2, 1, 2, nCols);
  const subCell = ws.getCell(2, 1);
  subCell.value = `Period: ${period || '—'}   |   ${sorted.length} incident${sorted.length !== 1 ? 's' : ''}   |   Generated ${fmtDate(new Date())}`;
  subCell.font = { italic: true, size: 10, color: { argb: 'FF475569' } };
  subCell.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(2).height = 18;

  // Row 3 spacer, row 4 headers
  const headerRow = ws.getRow(4);
  REGISTER_COLUMNS.forEach((c, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = c.header;
    cell.font = { bold: true, size: 9, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEF4444' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      right: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    };
  });
  headerRow.height = 34;

  // Data rows
  sorted.forEach((inc, i) => {
    const cat = categories.find(c => c.slug === inc.category);
    const typeLabel = TYPE_LABELS[inc.incident_type] || inc.incident_type || (cat?.name) || '';
    const reportedYn = inc.reported_to_ndis ? 'Yes' : 'No';
    const reported = inc.reported_to ? `${reportedYn} — ${inc.reported_to}` : reportedYn;
    const row = ws.addRow({
      ref:      inc.ref_no || (i + 1),
      date:     fmtDate(inc.incident_date),
      time:     inc.incident_time ? fmtTime12(inc.incident_time) : '',
      location: inc.location || '',
      type:     typeLabel,
      clients:  joinNames(inc.client_name, inc.involved_clients),
      staff:    joinNames(inc.staff_name || inc.assignee_name, inc.involved_staff),
      witness:  inc.witnesses || '',
      form:     inc.form_completed ? 'Yes' : 'No',
      reported,
      desc:     inc.description || '',
      actions:  inc.actions_taken || '',
      followup: inc.follow_up_notes || inc.resolution_notes || '',
      further:  inc.further_action || '',
      prevent:  inc.prevent_recurrence || '',
    });
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      cell.alignment = { vertical: 'top', wrapText: true, horizontal: col === 1 ? 'center' : 'left' };
      cell.font = { size: 9 };
      cell.border = {
        top: { style: 'hair', color: { argb: 'FFE2E8F0' } },
        left: { style: 'hair', color: { argb: 'FFE2E8F0' } },
        bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } },
        right: { style: 'hair', color: { argb: 'FFE2E8F0' } },
      };
    });
    if (i % 2 === 1) {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } };
      });
    }
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  triggerDownload(blob, `incident-register-${new Date().toISOString().slice(0, 10)}.xlsx`);
}
